import { patchState, signalStore, withComputed, withMethods, withProps, withState } from "@ngrx/signals";
import { inject } from "@angular/core";
import {
    AttendanceService,
    attendanceState,
    AttendanceStateName,
    GuestRegistrant,
    SetRegistrationConfig,
    UpdateAttendanceOptions,
} from "./attendance.service";
import { s25Resource } from "../../../util/s25.resource";
import { S25Util } from "../../../util/s25-util";
import { UserprefService } from "../../../services/userpref.service";
import { ContactService } from "../../../services/contact.service";
import { LoginService } from "../../../services/login.service";
import { SeOrgSecurityStore } from "../se.org.security.store";
import { S25Datefilter } from "../../s25-dateformat/s25.datefilter.service";
import { HttpDeleteRequestOptions, HttpPutRequestOptions } from "../../../dataaccess/data.access";
import { ModalService } from "../../modal/modal.service";
import { Attendance } from "./se.attendance.util";

type State = {
    eventId: number | undefined;
};

export const SeAttendanceStore = signalStore(
    withState<State>({ eventId: undefined }),
    withProps((store) => ({
        _attendanceService: inject(AttendanceService),
        _orgSecStore: inject(SeOrgSecurityStore),
    })),
    withProps((store) => ({
        _dateTimeFormat: s25Resource({ loader: () => UserprefService.getS25DateTimeformat() }),
        _contactId: s25Resource({ loader: () => ContactService.getCurrentId() }),
        _loggedIn: s25Resource({ loader: () => LoginService.getLoggedIn() }),
    })),
    withProps((store) => ({
        _userAttendance: s25Resource({
            if: () => store._loggedIn.value(),
            params: () => store.eventId(),
            loader: async ({ params: eventId, abortSignal }) => {
                return store._attendanceService.getUserAttendance(eventId, { abortSignal });
            },
        }),
        _publicCanRegister: s25Resource({
            if: () => store._loggedIn.value() === false && !!store.eventId(),
            params: () => store.eventId(),
            loader: async ({ params: eventId, abortSignal }) => {
                return store._attendanceService.canRegisterForEvent(eventId, { abortSignal });
            },
        }),
        _registrationConfig: s25Resource({
            params: () => store.eventId(),
            loader: async ({ params: eventId, abortSignal }) => {
                return store._attendanceService.getRegistrationConfig(eventId, { abortSignal });
            },
        }),
        _attendees: s25Resource({
            if: () => store._orgSecStore.canViewAttendees(),
            params: () => store.eventId(),
            loader: async ({ params: eventId, abortSignal }) => {
                return store._attendanceService.getAttendees(eventId, { abortSignal });
            },
        }),
    })),
    withProps((store) => ({
        _registrationForm: s25Resource({
            if: () => store._userAttendance.value()?.canRegister && !store._orgSecStore.canViewRegistration(),
            params: () => store.eventId(),
            loader: async ({ params: eventId, abortSignal }) => {
                return store._attendanceService.getRegistrationForm(eventId, { abortSignal });
            },
        }),
        _getAttendanceForContact: (contactId: number) => {
            const currentContact = store._contactId.value();
            if (!currentContact) return undefined;

            if (contactId === currentContact) {
                return store._userAttendance.value()?.attendance;
            }
            return store._attendees.value()?.find((attendee) => attendee.contactId === contactId)?.reservations;
        },
    })),
    withComputed((store) => ({
        userAttendance: () => store._userAttendance.value(),
        registrationFormId: () =>
            store._registrationConfig.lastValue()?.formId ?? store._registrationForm.value()?.ingestionPointId,
        canRegister: () =>
            store._loggedIn.value() ? !!store._userAttendance.value()?.canRegister : !!store._publicCanRegister.value(),
        registrationConfig: () => store._registrationConfig.lastValue(),
        attendees: () => {
            const attendees = store._attendees.value();
            const dateTimeFormat = store._dateTimeFormat.value();
            if (!attendees || !dateTimeFormat) return undefined;

            return attendees.map((attendee) => {
                return {
                    ...attendee,
                    reservations: attendee.reservations.map((reservation) => ({
                        ...reservation,
                        checkedIn: Attendance.State.isAtLeast(reservation.attendeeState, attendanceState.CHECKED_IN),
                        reservationStart: S25Datefilter.transform(reservation.reservationStart, dateTimeFormat),
                    })),
                };
            });
        },
    })),
    withMethods((store) => ({
        setEventId: (eventId: number) => {
            patchState(store, { eventId });
        },
        // reloadUserAttendance: () => store._userAttendance.resource.reload(),
        getAttendanceForContact: (contactId: number) => {
            return store.attendees()?.find((att) => att.contactId === contactId);
        },
        setRegistrationConfig: async (config: SetRegistrationConfig, httpOptions?: HttpPutRequestOptions) => {
            await store._attendanceService.setRegistrationConfig(config, httpOptions);
            store._userAttendance.resource.reload();
            store._registrationConfig.resource.reload();
            store._registrationForm.resource.reload();
            store._publicCanRegister.resource.reload();
        },
        deleteRegistrationConfig: async (httpOptions?: HttpDeleteRequestOptions) => {
            const eventId = store.eventId();
            if (!eventId) return;
            await store._attendanceService.deleteRegistrationConfig(eventId, httpOptions);
            store._userAttendance.resource.reload();
            store._registrationConfig.resource.reload();
            store._registrationForm.resource.reload();
            store._publicCanRegister.resource.reload();
        },
        registerGuest: async (request: GuestRegistrant) => {
            const eventId = store.eventId();
            if (!eventId) {
                throw new Error("Event is not loaded.");
            }

            return await store._attendanceService.registerGuest(eventId, request);
        },
        setAttendance: async (state: AttendanceStateName, options?: UpdateAttendanceOptions) => {
            const eventId = store.eventId();
            const currentContact = store._contactId.value();
            if (!eventId || !currentContact) return false;

            // If we are cancelling pending registrations, confirm that the user wants to cancel the registration
            // for all occurrences in those workflows
            let occurrences = options?.occurrences;
            if (occurrences?.length) {
                const occIds = new Set(occurrences);
                const attendance = store._getAttendanceForContact(options?.contactId ?? currentContact);
                if (!!attendance) {
                    const occsToCancel = attendance.filter((att) => {
                        if (!occIds.has(att.reservationId)) return false;
                        if (att.attendeeState !== attendanceState.PENDING) return false;
                        return true;
                    });
                    if (!!occsToCancel.length) {
                        const submissions = new Set(occsToCancel.map((occ) => occ.registrationSubmissionId!));
                        const occsForSubmission = attendance.filter((att) =>
                            submissions.has(att.registrationSubmissionId!),
                        );

                        if (occsForSubmission.length > occsToCancel.length) {
                            const yes = await ModalService.modal("confirm-cancel-registrations", {
                                title: "Cancel Registration Requests",
                                occurrences: occsForSubmission,
                            });
                            if (!yes) return false;
                            occurrences = occsForSubmission.map((occ) => occ.reservationId);
                        }
                    }
                }
            }

            // Update attendance
            const [_, err] = await S25Util.Maybe(
                store._attendanceService.updateAttendance(eventId, state, { ...options, occurrences }),
            );
            if (!!err) {
                S25Util.showError(err);
                return false;
            }

            if (!options?.contactId || options.contactId === store._contactId.value()) {
                store._userAttendance.resource.reload(); // Reload own attendance
            }
            store._attendees.resource.reload(); // Reload attendees
            return true;
        },
    })),
);
